/**
 * Browser Lock Manager
 *
 * Generic manager for handling browser locks:
 * - Fullscreen API
 * - Pointer Lock API
 * - Keyboard Lock API
 *
 * Handles browser-specific quirks (Firefox, Safari, mobile) and provides
 * unified interface for requesting locks and monitoring lock state.
 *
 * This is a generic utility that can be used by any feature.
 */

/**
 * Check if device is mobile (touch-based)
 * Mobile devices don't support pointer lock and use touch input instead
 */
function isMobileDevice(): boolean {
  const userAgent = navigator.userAgent.toLowerCase();
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const isMobileUA = /iphone|ipod|android|blackberry|windows phone/g.test(userAgent);
  const isTablet = /(ipad|tablet|playbook|silk)|(android(?!.*mobile))/g.test(userAgent);

  return isTouchDevice && (isMobileUA || isTablet);
}

export interface BrowserLockConfig {
  /**
   * Callback when fullscreen state changes
   */
  onFullscreenChange?: (isFullscreen: boolean) => void;

  /**
   * Callback when pointer lock state changes
   */
  onPointerLockChange?: (isLocked: boolean) => void;

  /**
   * Callback when visibility changes (tab hidden/shown)
   */
  onVisibilityChange?: (isVisible: boolean) => void;
}

/**
 * Manager for browser lock APIs
 * Handles fullscreen, pointer lock, and keyboard lock with browser-specific fallbacks
 */
export class BrowserLockManager {
  private config: BrowserLockConfig;
  private cleanupFunctions: (() => void)[] = [];

  constructor(config: BrowserLockConfig = {}) {
    this.config = config;
  }

  /**
   * Initialize lock state monitoring
   * Sets up event listeners for state changes
   */
  public init(): void {
    console.log('[BrowserLockManager] Initializing lock state monitoring');

    // Fullscreen change listener
    if (this.config.onFullscreenChange) {
      const fullscreenHandler = () => {
        const isFullscreen = !!document.fullscreenElement;
        console.log(`[BrowserLockManager] Fullscreen changed: ${isFullscreen}`);
        this.config.onFullscreenChange?.(isFullscreen);
      };
      document.addEventListener('fullscreenchange', fullscreenHandler);
      this.cleanupFunctions.push(() => {
        document.removeEventListener('fullscreenchange', fullscreenHandler);
      });
      console.log('[BrowserLockManager] Fullscreen listener registered');
    }

    // Pointer lock change listener
    if (this.config.onPointerLockChange) {
      const pointerLockHandler = () => {
        const isLocked = !!document.pointerLockElement;
        this.config.onPointerLockChange?.(isLocked);
      };
      document.addEventListener('pointerlockchange', pointerLockHandler);
      this.cleanupFunctions.push(() => {
        document.removeEventListener('pointerlockchange', pointerLockHandler);
      });
    }

    // Visibility change listener
    if (this.config.onVisibilityChange) {
      const visibilityHandler = () => {
        const isVisible = !document.hidden;
        this.config.onVisibilityChange?.(isVisible);
      };
      document.addEventListener('visibilitychange', visibilityHandler);
      this.cleanupFunctions.push(() => {
        document.removeEventListener('visibilitychange', visibilityHandler);
      });
    }
  }

  /**
   * Request all browser locks (fullscreen, pointer lock, keyboard lock)
   * Handles browser-specific quirks and provides graceful fallbacks
   *
   * This method is designed to NEVER throw - it will always degrade gracefully
   * if any locks fail, which is expected on many mobile browsers.
   */
  public async requestLocks(): Promise<void> {
    try {
      const isSafari = this.isSafari();
      const isFirefox = this.isFirefox();

      // Request fullscreen FIRST, then pointer lock.
      // Chrome requires fullscreen to be active before pointer lock works properly.
      // iPhone Safari doesn't support the Fullscreen API at all, so skip it there.
      // iPad Safari DOES support fullscreen — only skip when the API is truly absent.
      const fullscreenAvailable = document.fullscreenEnabled
        || !!(document as Document & { webkitFullscreenEnabled?: boolean }).webkitFullscreenEnabled;
      if (!isSafari || fullscreenAvailable) {
        await this.requestFullscreen();
      }

      let oldRequest = false;

      // Try modern permission API first
      if ('permissions' in navigator) {
        try {
          // Check permissions (wrapped in try-catch as some browsers throw on unsupported permissions)
          // Skip pointer lock on mobile devices
          if (!isMobileDevice()) {
            try {
              const pointerLock = await navigator.permissions.query({ name: 'pointer-lock' as PermissionName });
              // Request pointer lock AFTER fullscreen is active
              if (pointerLock.state === 'granted' || pointerLock.state === 'prompt') {
                await this.requestPointerLock();
              }
            } catch (e) {
              console.log('[BrowserLockManager] Pointer lock permission query not supported, trying direct request');
              await this.requestPointerLock();
            }
          }

          // Skip keyboard lock on mobile devices (not supported)
          if (!isMobileDevice()) {
            try {
              const keyboardLock = await navigator.permissions.query({ name: 'keyboard-lock' as PermissionName });
              // Request keyboard lock
              if (keyboardLock.state === 'granted' || keyboardLock.state === 'prompt') {
                await this.requestKeyboardLock();
              }
            } catch (e) {
              console.log('[BrowserLockManager] Keyboard lock permission query not supported, trying direct request');
              await this.requestKeyboardLock();
            }
          }
        } catch (e) {
          console.log('[BrowserLockManager] Permission API error, using fallback');
          oldRequest = true;
        }
      } else {
        oldRequest = true;
      }

      // Fallback for Safari or if permission API fails
      if (oldRequest) {
        // Skip pointer lock on mobile devices
        if (!isMobileDevice()) {
          await this.requestPointerLock();
        }
        // Skip keyboard lock on mobile devices
        if (!isMobileDevice()) {
          await this.requestKeyboardLock();
        }
      }
    } catch (error) {
      // Top-level catch to ensure we NEVER throw and block the UI
      // This is expected to fail gracefully on many mobile browsers
      console.warn('[BrowserLockManager] requestLocks failed, continuing with degraded experience:', error);
    }
  }

  /**
   * Request fullscreen mode
   * Falls back to no options if { navigationUI: 'hide' } fails (not supported on some mobile browsers)
   */
  public async requestFullscreen(): Promise<void> {
    try {
      if (!document.fullscreenElement) {
        try {
          // Try with navigationUI option first (hides browser UI for better immersion)
          await document.documentElement.requestFullscreen({ navigationUI: 'hide' });
        } catch (e) {
          // Fallback: try without options if navigationUI not supported
          console.log('[BrowserLockManager] navigationUI option not supported, trying without options');
          await document.documentElement.requestFullscreen();
        }
      }
    } catch (e) {
      console.log('[BrowserLockManager] Fullscreen not supported or denied:', e);
    }
  }

  /**
   * Request pointer lock
   * Note: Mobile devices don't support pointer lock and it's not needed for touch input
   */
  public async requestPointerLock(): Promise<void> {
    // Skip pointer lock on mobile devices (not supported and not needed for touch)
    if (isMobileDevice()) {
      console.log('[BrowserLockManager] Skipping pointer lock (mobile device)');
      return;
    }

    try {
      if (!document.pointerLockElement) {
        // CRITICAL: Request pointer lock on the SAME element as fullscreen (documentElement)
        // Chrome requires pointer lock to be on the fullscreen element, not body
        // This prevents the mouse from escaping at the edges
        try {
          await document.documentElement.requestPointerLock({ unadjustedMovement: true });
        } catch (e: unknown) {
          // Fallback: try without unadjustedMovement if it fails
          // Some browsers/devices don't support this option
          console.log('[BrowserLockManager] unadjustedMovement not supported, falling back to standard pointer lock');
          await document.documentElement.requestPointerLock();
        }
      }
    } catch (e: unknown) {
      if (e instanceof Error && e.name === 'InUseAttributeError') {
        console.log('[BrowserLockManager] Pointer lock already in progress');
      } else {
        console.log('[BrowserLockManager] Pointer lock error:', e);
      }
    }
  }

  /**
   * Request keyboard lock
   * Note: Mobile devices don't support keyboard lock (they use touch/virtual keyboards)
   */
  public async requestKeyboardLock(): Promise<void> {
    // Skip keyboard lock on mobile devices (not supported and not needed for touch)
    if (isMobileDevice()) {
      console.log('[BrowserLockManager] Skipping keyboard lock (mobile device)');
      return;
    }

    try {
      const nav = navigator as Navigator & { keyboard?: { lock: () => Promise<void> } };
      if (nav.keyboard?.lock) {
        await nav.keyboard.lock();
      }
    } catch (e) {
      console.log('[BrowserLockManager] Keyboard lock not supported');
    }
  }

  /**
   * Exit fullscreen mode
   */
  public async exitFullscreen(): Promise<void> {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      }
    } catch (e) {
      console.log('[BrowserLockManager] Exit fullscreen failed:', e);
    }
  }

  /**
   * Exit pointer lock
   */
  public exitPointerLock(): void {
    if (document.pointerLockElement) {
      document.exitPointerLock();
    }
  }

  /**
   * Release all browser locks (fullscreen, pointer lock)
   * Useful when transitioning to states that need user interaction
   *
   * This method is designed to NEVER throw - it will always degrade gracefully
   */
  public async releaseAllLocks(): Promise<void> {
    try {
      console.log('[BrowserLockManager] Releasing all browser locks');

      // Exit pointer lock (synchronous)
      this.exitPointerLock();

      // Exit fullscreen (async)
      await this.exitFullscreen();

      // Note: Keyboard lock is automatically released when pointer/fullscreen are released
    } catch (error) {
      console.warn('[BrowserLockManager] releaseAllLocks failed, continuing anyway:', error);
    }
  }

  /**
   * Check if currently in fullscreen
   */
  public isFullscreen(): boolean {
    return !!document.fullscreenElement;
  }

  /**
   * Check if pointer is locked
   */
  public isPointerLocked(): boolean {
    return !!document.pointerLockElement;
  }

  /**
   * Check if tab is visible
   */
  public isVisible(): boolean {
    return !document.hidden;
  }

  /**
   * Cleanup all event listeners
   */
  public cleanup(): void {
    this.cleanupFunctions.forEach(cleanup => cleanup());
    this.cleanupFunctions = [];
  }

  /**
   * Detect Safari browser
   */
  private isSafari(): boolean {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  }

  /**
   * Detect Firefox browser
   */
  private isFirefox(): boolean {
    return /firefox/i.test(navigator.userAgent);
  }
}

